import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the deepfake-whaling start screen. Three
 * short slides (what/who, red flags, key terms), each with a bespoke animated
 * flat SVG built on the shared reader art toolkit. Content only; the reader UI
 * lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. live video call from the "CEO" with an urgent payment request    */
/* ------------------------------------------------------------------ */
function VideoCallArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: video call")} aria={tx("A live video call from someone who looks like the CEO, asking for an urgent wire transfer")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Video call")}
      </text>
      <rect x="262" y="37" width="82" height="24" rx="12" fill={C.panel2} stroke={C.line} />
      <circle className="sr-anim-blink" cx="278" cy="49" r="4" fill={C.red} />
      <text x="289" y="53.2" fontSize="11" fontWeight="700" fill={C.soft} letterSpacing="0.8">
        {tx("LIVE")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="122" rx="8" fill={C.code} stroke={C.line} />
      <g className="sr-anim-float">
        <circle cx="180" cy="124" r="20" fill={C.panel2} stroke={C.line} />
        <path d="M138 202 q0 -40 42 -40 q42 0 42 40 z" fill={C.panel2} stroke={C.line} />
      </g>
      <g fill="none" stroke={C.amber} strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.8">
        <path d="M154 108 v-8 h8" />
        <path d="M206 100 h8 v8" />
        <path d="M154 140 v8 h8" />
        <path d="M206 148 h8 v-8" />
      </g>
      <text x="27" y="192" fontSize="10.5" fill={C.soft}>
        {tx("Emma R., CEO")}
      </text>
      <rect x="258" y="90" width="78" height="22" rx="11" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <circle className="sr-anim-ring" cx="272" cy="101" r="4" fill="none" stroke={C.amber} strokeWidth="1.4" />
      <circle cx="272" cy="101" r="1.6" fill={C.amber} />
      <text x="282" y="105" fontSize="10.5" fontWeight="700" fill={C.amber}>
        {tx("Is it her?")}
      </text>

      <rect x="16.5" y="214.5" width="327" height="66" rx="8" fill={C.amber} fillOpacity="0.08" stroke={C.amber} strokeOpacity="0.4" />
      <text x="30" y="234" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Approve a wire transfer")}
      </text>
      <text x="30" y="251" fontSize="11" fill={C.muted}>
        {tx("EUR 850,000 to a new vendor, foreign bank")}
      </text>
      <text x="30" y="268" fontSize="11" fontWeight="600" fill={C.amber}>
        {tx("Due today, before 17:00")}
      </text>
      <rect x="276" y="224" width="56" height="20" rx="10" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.45" />
      <text x="304" y="237.8" fontSize="10" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.6">
        {tx("URGENT")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. spoofed email: address mismatch + four pressure flags            */
/* ------------------------------------------------------------------ */
function Flag({ x, y, label, delay }: { x: number; y: number; label: string; delay: string }): JSX.Element {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x="0.5" y="0.5" width="157" height="27" rx="8" fill={C.panel2} stroke={C.line} />
      <circle className="sr-anim-ring" cx="15" cy="14" r="4" fill="none" stroke={C.red} strokeWidth="1.4" style={{ animationDelay: delay }} />
      <circle cx="15" cy="14" r="1.8" fill={C.red} />
      <text x="28" y="18" fontSize="11" fill={C.text}>
        {txv(label)}
      </text>
    </g>
  );
}

function SpoofedEmailArt(): JSX.Element {
  const H = 290;
  return (
    <Frame label={tx("Example: the email before the call")} aria={tx("An urgent email from the CEO whose sender address is on an outside domain, with four pressure flags: urgent, secret, a call is coming, and a new vendor")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="13.5" fontWeight="700" fill={C.text}>
        {tx("URGENT — Need your help today")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <text x="16" y="88" fontSize="10.5" fill={C.muted}>
        {tx("From")}
      </text>
      <text x="52" y="88" fontSize="12.5" fontWeight="600" fill={C.text}>
        {tx("Emma Richardson")}
      </text>
      <rect className="sr-anim-pulse" x="12" y="96" width="336" height="20" rx="5" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.5" />
      <text x="17" y="110" fontSize="9.5" fontFamily={MONO} fill={C.red}>
        emma.richardson.executive@consulting-group-global.com
      </text>
      <text x="16" y="132" fontSize="10.5" fontWeight="600" fill={C.red}>
        {tx("Not your company domain")}
      </text>

      <rect x="16.5" y="142.5" width="327" height="58" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="164" fontSize="11.5" fill={C.soft}>
        {tx("Please remain available. I will call you")}
      </text>
      <text x="29" y="182" fontSize="11.5" fill={C.soft}>
        {tx("shortly on Teams. Keep this between us.")}
      </text>
      <Flag x={16} y={214} label={tx("Urgent: today")} delay="0s" />
      <Flag x={186} y={214} label={tx("Secret: keep it quiet")} delay="0.5s" />
      <Flag x={16} y={248} label={tx("A call is coming")} delay="1s" />
      <Flag x={186} y={248} label={tx("New vendor, foreign bank")} delay="1.5s" />
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. verify through a channel the caller does not control             */
/* ------------------------------------------------------------------ */
function StepRow({ y, n, label, pulse }: { y: number; n: string; label: string; pulse?: boolean }): JSX.Element {
  return (
    <g>
      <rect x="16.5" y={y + 0.5} width="327" height="31" rx="8" fill={C.panel2} stroke={C.line} />
      <circle
        className={pulse ? "sr-anim-pulse" : undefined}
        cx="34"
        cy={y + 16}
        r="10"
        fill={C.mint}
        fillOpacity="0.14"
        stroke={C.mint}
        strokeOpacity="0.4"
      />
      <text x="34" y={y + 20} fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {txv(n)}
      </text>
      <text x="54" y={y + 20} fontSize="12" fill={C.text}>
        {txv(label)}
      </text>
    </g>
  );
}

function VerifyArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label={tx("Example: how to verify")} aria={tx("A video call marked as fakeable next to a saved phone number marked as trusted, then four steps: ask a private question, end the call, call back on a known number, report it")} height={H}>
      <rect x="16.5" y="36.5" width="155" height="68" rx="10" fill={C.red} fillOpacity="0.07" stroke={C.red} strokeOpacity="0.45" />
      <g fill="none" stroke={C.red} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="28" y="50" width="16" height="12" rx="2.5" />
        <polyline points="44,54 50,50.5 50,61.5 44,58" />
      </g>
      <text x="58" y="60" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Video call")}
      </text>
      <text x="28" y="82" fontSize="10.5" fill={C.muted}>
        {tx("Face and voice")}
      </text>
      <text x="28" y="96" fontSize="10.5" fontWeight="600" fill={C.red}>
        {tx("can be cloned")}
      </text>

      <polyline points="175,70 184,70" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="181,66.5 185,70 181,73.5" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

      <rect x="188.5" y="36.5" width="155" height="68" rx="10" fill={C.mint} fillOpacity="0.07" stroke={C.mint} strokeOpacity="0.45" />
      <g fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="203" y="49" width="10" height="15" rx="2.2" />
        <line x1="206.5" y1="60.5" x2="209.5" y2="60.5" />
      </g>
      <text x="224" y="60" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Known number")}
      </text>
      <text x="200" y="82" fontSize="10.5" fill={C.muted}>
        {tx("Or in person")}
      </text>
      <text x="200" y="96" fontSize="10.5" fontWeight="600" fill={C.mint}>
        {tx("you control it")}
      </text>
      <circle className="sr-anim-ring" cx="328" cy="50" r="6" fill="none" stroke={C.mint} strokeWidth="1.4" />
      <polyline points="325,50 327.4,52.6 331.4,47.4" fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

      <text x="180" y="127" fontSize="10.5" fill={C.muted} textAnchor="middle">
        {tx("Verify where the caller has no control")}
      </text>

      <StepRow y={138} n="1" label={tx("Ask what only the real person knows")} />
      <StepRow y={176} n="2" label={tx("End the call")} />
      <StepRow y={214} n="3" label={tx("Call back on a number you already have")} />
      <StepRow y={252} n="4" label={tx("Report it to security")} pulse />
    </Frame>
  );
}

export const DEEPFAKE_WHALING_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Seeing and hearing is no longer proof.",
    illustration: VideoCallArt,
    caption: "A familiar face asking for money is a reason to check.",
    sections: [
      {
        h: "What this is about",
        body: "You\u2019re a finance manager on holiday. An urgent email from the CEO is followed by a live video call asking you to approve a \u20AC850,000 transfer to a new vendor.",
      },
      {
        h: "Who it\u2019s for",
        body: "Finance teams, executives, assistants and managers \u2014 anyone who can approve or release a payment.",
      },
      {
        h: "Why your judgement matters",
        body: "A cloned face and voice can pass for the real person, and no filter ends the call for you. Once the money leaves for a foreign account it is usually gone in minutes. Checking through a channel the caller doesn\u2019t control is a human decision. Yours.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "The request is built to be obeyed.",
    illustration: SpoofedEmailArt,
    caption: "Read the address, not the display name.",
    sections: [
      {
        h: "A sender that only looks right",
        body: "The display name says CEO, but the address sits on an outside domain. Read the address before you trust the name.",
      },
      {
        h: "Urgency, secrecy and a call that\u2019s coming",
        body: "\u201CToday\u201D and \u201Ckeep this between us\u201D remove the time and the people who would catch it. Announcing a call makes the fake video feel expected.",
      },
      {
        h: "Tells in the video",
        body: "Odd blinking, lips a beat behind the words, smeared teeth, shimmering hair edges, a metallic voice. A clean call proves nothing.",
      },
      {
        h: "A payment outside the process",
        body: "New vendor, foreign bank, no procurement approval. The \u201Cexception\u201D is the attack.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: VerifyArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Whaling", body: "Phishing aimed at senior people, or in their name, to unlock large payments or sensitive access." },
      { h: "Deepfake", body: "Video or audio made or altered by AI to imitate a real person\u2019s face and voice." },
      { h: "Executive impersonation", body: "Posing as a senior leader so staff obey quickly and don\u2019t question the request." },
      { h: "Challenge question", body: "A private question only the real person could answer. A dodge is a warning sign." },
      { h: "Out-of-band check", body: "Confirming a request through a separate channel you already trust, such as a saved number or in person." },
      { h: "Lip-sync drift", body: "Mouth movement that lags slightly behind the spoken words." },
    ],
  },
];
